import $ from 'jquery';
import Uppy from '@uppy/core';
import UppyFileInput from '@uppy/file-input';
import UppyS3 from '@uppy/aws-s3';
import emitter from 'namespace-emitter';
import { v4 as uuidv4 } from 'uuid';

import { API } from './api';
import { renderTpl, compileTemplate } from './helpers.js';

// Files dropped on (or picked through) the media-kit zone carry this source through Uppy, so
// `handleBatchUploadEachFile` can decide media-kit membership per file. It is the only way into the
// kit — the gallery modal stopped selecting for it (ADR-008).
const MEDIA_KIT_DROP_SOURCE = 'method:mediaKitDrop';

// An in-flight file has no server metadata yet, so `describeFile` (api.js) cannot resolve its icon
// tile; the kinds it can tell apart from a browser MIME type alone are mirrored here.
const PENDING_FILE_KINDS = [
  { match: type => /^image\//.test(type), kind: 'image', icon: 'mdi-file-image-outline' },
  { match: type => type === 'application/pdf', kind: 'pdf', icon: 'mdi-file-pdf-box' },
];
const PENDING_FILE_KIND_FALLBACK = { kind: 'file', icon: 'mdi-file-document-outline' };

// Server-side Shrine validates these; the browser enforced nothing until now, so a 30 MB photo
// travelled all the way to S3 before being refused. The drop-zone copy names both limits, so the
// client has to hold them too.
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_FILE_SIZE = 30 * 1024 * 1024;
// Width and height in pixels: ImageUploader::MAX_DIMENSIONS, named by the Body hint.
const MAX_IMAGE_DIMENSION = 4096;

// `{width, height}` from the image header, or null when the browser cannot read the file (HEIC
// outside Safari, a corrupt file): the server's own check decides those.
const readImageDimensions = file => new Promise(resolve => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  const done = result => {
    URL.revokeObjectURL(url);
    resolve(result);
  };
  img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight });
  img.onerror = () => done(null);
  img.src = url;
});

// Rejection toasts are rendered raw (`{{{message}}}` in the notifier template) and carry the
// offending file name.
const escapeHtml = text => String(text).replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]));

export class Uploader {
  defaultConfig = {
    uppy: {
      autoProceed: true,
      allowMultipleUploads: true,
      logger: Uppy.debugLogger,
      restrictions: {
        maxNumberOfFiles: null,
        allowedFileTypes: [
          /* images */ '.jpg', '.png', '.gif', 'image/*',
          /* pdfs */ '.pdf', 'application/pdf',
          /* rich text */ '.doc', '.docx', '.odt', '.rtf', 'application/msword', 'application/rtf', 'application/odt', 'application/vnd.oasis.opendocument.text', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          /* spreadsheets */ '.xls', '.xlsx', '.ods', 'application/vnd.ms-excel', 'application/vnd.oasis.opendocument.spreadsheet', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          /* presentations */ '.ppt', '.pptx', '.odp', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.presentation', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          // /* zips */ '.zip',
        ],
      },
      onBeforeFileAdded: file => {
        if (file.source === 'method:uploadImage' && !file.type.match(/^image\//)) {
          return false;
        }
      },
    },
    uppyOneImage: {
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: [
          /* images */ '.jpg', '.png', '.gif', 'image/*',
        ],

      },
    },
    uppyContentFile: {
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: [
          // /* pdfs */ '.pdf', 'application/pdf',  disabled for now due to a business request
          /* rich text */ '.doc', '.docx', '.odt', '.rtf', 'application/msword', 'application/rtf', 'application/odt', 'application/vnd.oasis.opendocument.text', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          /* presentations */ '.ppt', '.pptx', '.odp', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.presentation', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ],
      },
    },
    UppyFileInput: {
      pretty: true,
    },
    uppyS3: {
      companionUrl: API.endpoints.uploads.companionUrl,
    },
  };

  constructor({
    target, // selector or DOM element inside od which the uploader should render
    list = {}, // {source: 'Handlebars template code', target: DOMElement}
    mediaKitList = {}, // {source: 'Handlebars template code', target: DOMElement}
    id = 'uppy', // unique id of uploader widget, id should be constant between reloads (for example "release-creation")
    meta = {}, // meta fields attached to every file
    restrictions = {},
    onUploadSuccess,
    isFileUsed,
    onFileDeletion,
    initialImages = [],
    initialAttachments = [],
    initialMediaKit = [],
    notifier,
    // selectionLimit = 0,
  }) {
    if (!target) return null;

    this.list = { ...list };
    this.mediaKitList = { ...mediaKitList };
    this.id = id;
    this.meta = meta;
    this.restrictions = restrictions;
    this.onUploadSuccess = onUploadSuccess;
    this.isFileUsed = isFileUsed;
    this.onFileDeletion = onFileDeletion;
    this.events = emitter();
    this.notifier = notifier;
    // this.selectionLimit = limit;

    this.files = { // uploaded files
      images: [],
      attachments: [],
    };

    this.inTransit = {
      images: [],
      attachments: [],
    };

    // Rows for files that exist in the browser and nowhere else yet, keyed by Uppy file id.
    // `inTransit` is filled at `'complete'` — that is the save leg; this covers the upload leg.
    this.pendingMediaKit = new Map();

    // `library/id` for every file this visit uploaded, filled once the save resolves an id for it
    // (`parseUpdatedFile`). The media-kit row's "Uploaded" reads from it and from nothing else: the
    // state belongs to the act of uploading, not to the file, so it lasts exactly as long as the
    // page does. An Uploader is built per load (media-gallery.js, from `turbolinks:load`), so a
    // reload — Turbolinks visit included — starts with the set empty and the rows plain.
    this.justUploaded = new Set();

    this.initSavedImages(initialImages, initialAttachments, initialMediaKit);
    this.initElements(target);
    this.initLists();
    this.initUppy();
  }

  initSavedImages(initialImages, initialAttachments, initialMediaKit) {
    initialImages.forEach(file => {
      this.files.images.push({ ...file, selected: false, library: 'images' });
    });
    initialAttachments.forEach(file => {
      this.files.attachments.push({ ...file, selected: false, library: 'attachments' });
    });
    initialMediaKit.forEach(fileRef => {
      const fileDef = this.files[fileRef.library].find(file => file.id === fileRef.id);
      if (fileDef) {
        fileDef.inMediaKit = true;
      }
    });
  };

  insertElement(parent, tag, className) {
    const elem = document.createElement(tag);
    Array.isArray(className) ? elem.classList.add(...className) : className && elem.classList.add(className);
    parent.append(elem);
    return elem;
  };

  initElements(target) {
    this.dom = {
      target: typeof target === 'string' ? document.querySelector(target) : target,
    };

    this.dom.uploadButton = this.insertElement(this.dom.target, 'div', 'uploader-button');
    this.dom.progress = this.insertElement(this.dom.target, 'div', 'uploader-progress');

    this.dom.confirmDeletion = document.querySelector('#media-gallery-deletion-warning');
    this.dom.confirmDeletionMessage = this.dom.confirmDeletion.querySelector('.message');

    this.dom.confirmDeletion.messageData = JSON.parse(this.dom.confirmDeletion.querySelector('.message-data').innerHTML);
    this.dom.confirmDeletion.setContent = (filename, reasons = []) => {
      const data = this.dom.confirmDeletion.messageData;
      let msg = '';

      const reasonTexts = reasons.map(key => data.reasons[key]).filter(Boolean);

      if (reasonTexts.length === 1) {
        msg = data.template.replace('{{reasons}}', reasonTexts[0]);
      } else if (reasonTexts.length > 1) {
        msg = data.template.replace('{{reasons}}', reasonTexts.slice(0, -1).join(data.infix) + data.lastInfix + reasonTexts.slice(-1));
      }

      // The filename is the uploader's own input — escaped and emphasised, never raw HTML.
      this.dom.confirmDeletionMessage.innerHTML =
        msg.replace('{{filename}}', `<strong>${escapeHtml(filename)}</strong>`);
    };

    if (typeof jQuery !== 'undefined' && typeof jQuery.fn.modal !== 'undefined') {
      this.dom.confirmDeletion.showModal = () => $(this.dom.confirmDeletion).modal('show');
      this.dom.confirmDeletion.hideModal = () => $(this.dom.confirmDeletion).modal('hide');

      $(this.dom.confirmDeletion).on('hide.bs.modal', e => {
        if (typeof this.dom.confirmDeletion.action === 'function') {
          this.dom.confirmDeletion.action(false);
        }
      });
    } else {
      this.dom.confirmDeletion.showModal = () => this.dom.confirmDeletion.classList.add('show');
      this.dom.confirmDeletion.hideModal = () => {
        this.dom.confirmDeletion.classList.remove('show')
        if (typeof this.dom.confirmDeletion.action === 'function') {
          this.dom.confirmDeletion.action(false);
        }
      };
      Array.from(this.dom.confirmDeletion.querySelectorAll('[data-dismiss="modal"')).forEach(elem => {
        elem.addEventListener('click', () => this.dom.confirmDeletion.hideModal());
      })
    }

    this.dom.confirmDeletion.querySelector('.action-button').addEventListener('click', e => {
      e.preventDefault();
      if (typeof this.dom.confirmDeletion.action === 'function') {
        this.dom.confirmDeletion.action(true);
        this.dom.confirmDeletion.hideModal();
      }
    });

    this.initMediaKitModals();
  };

  // The two media-kit row popups (releases/form/_modals). They are static markup filled in per row,
  // and both are optional: ActiveAdmin builds an Uploader on a page that has neither.
  initMediaKitModals() {
    this.dom.mediaKitPreview = this.wrapModal(document.querySelector('#media-kit-preview-modal'));
    this.dom.mediaKitDescription = this.wrapModal(document.querySelector('#media-kit-description-modal'));

    if (this.dom.mediaKitPreview) {
      this.dom.mediaKitPreviewTitle = this.dom.mediaKitPreview.elem.querySelector('.media-kit-preview-title');
      this.dom.mediaKitPreviewImage = this.dom.mediaKitPreview.elem.querySelector('.media-kit-preview-image');
    }

    if (this.dom.mediaKitDescription) {
      this.dom.mediaKitDescriptionFile = this.dom.mediaKitDescription.elem.querySelector('.media-kit-description-file');
      this.dom.mediaKitDescriptionField = this.dom.mediaKitDescription.elem.querySelector('.media-kit-description-field');
      this.dom.mediaKitDescription.elem
        .querySelector('.media-kit-description-save')
        .addEventListener('click', this.handleMediaKitDescriptionSave);
    }
  };

  // Bootstrap's modal plugin rides on jQuery, exactly as the deletion warning above assumes; the
  // classList fallback is there so a page without it degrades instead of throwing.
  //
  // `hide` carries the same guard as `MediaGallery#hideModal`: Bootstrap 4's `Modal#hide` bails out
  // while `_isTransitioning` is set, so a close issued during the opening fade is dropped without a
  // trace — and saving a description is one click after opening the popup. A dropped hide is
  // re-issued once the pending transition reports `shown`.
  wrapModal(elem) {
    if (!elem) return null;

    const withJquery = typeof jQuery !== 'undefined' && typeof jQuery.fn.modal !== 'undefined';

    return {
      elem,
      show: () => (withJquery ? $(elem).modal('show') : elem.classList.add('show')),
      hide: () => {
        if (!withJquery) {
          elem.classList.remove('show');
          return;
        }

        const $elem = $(elem);
        $elem.modal('hide');

        if (elem.classList.contains('show')) {
          $elem.one('shown.bs.modal', () => $elem.modal('hide'));
        }
      },
    };
  };

  initUppy() {
    this.uppyOptions = {
      ...this.defaultConfig.uppy,
      restrictions: {
        ...this.defaultConfig.uppy.restrictions,
        ...this.restrictions,
      },
      id: this.id,
      meta: this.meta,
    };

    this.uppy = Uppy(this.uppyOptions).use(UppyFileInput, {
      ...this.defaultConfig.UppyFileInput,
      target: this.dom.uploadButton,
      locale: {
        strings: {
          chooseFiles: this.dom.target.dataset.label || 'Upload',
        },
      },
    }).use(UppyS3, {
      ...this.defaultConfig.uppyS3,
    });

    // Every route in (the gallery's file input, the media-kit zone) ends in Uppy, and a
    // preprocessor runs before the S3 upload and may be async, which reading dimensions needs.
    this.uppy.addPreProcessor(this.refuseOversizedImages);

    this.uppy.on('file-added', file => {
      // A new batch: the previous one's refusal may give way to its progress again.
      this.refusalShown = false;
      this.updateProgress(this.uppy.getState().totalProgress * 0.8 + 10);
      this.trackPendingMediaKitFile(file);
    });

    this.uppy.on('upload-progress', (file, progress) => {
      this.updateProgress(this.uppy.getState().totalProgress * 0.8 + 10);
      this.updatePendingMediaKitProgress(file, progress);
    });

    this.uppy.on('upload-error', file => this.forgetPendingMediaKitFile(file));
    this.uppy.on('file-removed', file => this.forgetPendingMediaKitFile(file));

    this.uppy.on('complete', result => {
      // console.log('result:', result)
      if (result.successful.length) {
        const connectionId = uuidv4();
        result.successful.forEach((file) => {
          this.handleBatchUploadEachFile(file, connectionId);
          this.holdPendingMediaKitFile(file, connectionId);
        });
        this.handleBatchUploadSuccess(100, connectionId);
      } else {
        let limitExceeded = false;
        try {
          if (result.failed[0].error === 'Your proposed upload exceeds the maximum allowed size') {
            limitExceeded = true;
          }
        } catch (e) { }
        console.warn('upload failed', result.failed);
        this.updateProgress(-1, limitExceeded);
      }
    });
  };

  uploadContentFile() {
    this.uploadingContentFile = true;
    this.uppy.setOptions(this.defaultConfig.uppyContentFile);
    setTimeout(() => {
      this.dom.uploadButton.querySelector('.uppy-FileInput-btn').dispatchEvent(new Event('click'));
    }, 50);
  };

  resetOptions() {
    this.uploadingContentFile = false;
    this.uppy.setOptions(this.uppyOptions);
  };

  updateProgress(percentage, limitExceeded) {
    // A refusal names files the writer still has to fix; the rest of its batch uploading, or
    // succeeding, must not replace it. A failure still does.
    if (this.refusalShown && percentage > -1) return;

    if (this.notifier) {
      if (percentage >= 100) {
        this.notifier.show('upload-success');
      } else if (percentage > -1) {
        this.notifier.show('upload-progress', { progress: percentage });
      } else if (limitExceeded) {
        this.notifier.show('upload-errorLimit');
      } else {
        this.notifier.show('upload-error');
      }
    } else {
      this.dom.progress.innerHTML = percentage > -1 ? parseInt(percentage) + '%' : '';
    }
  };

  handleBatchUploadEachFile = (file, connectionId) => {
    const data = {
      ...API.parseFile.fromUppy(file),
      connectionId,
      selected: true,
      inMediaKit: file.source === MEDIA_KIT_DROP_SOURCE,
      isContent: !!this.uploadingContentFile,
    };

    if (/^image\//.test(file.type)) {
      this.inTransit.images.push(data);
    } else {
      this.inTransit.attachments.push(data);
    }
  };

  handleBatchUploadSuccess(setProgress = 100, connectionId) {
    if (typeof this.onUploadSuccess === 'function') {
      this.onUploadSuccess(this.inTransit).then((res) => {
        this.updateFiles(connectionId, res, setProgress);
      }).catch((err) => {
        console.warn('error handling upload (handleBatchUploadSuccess):', err);
        let limitExceeded = false;
        try { limitExceeded = limitExceeded || err.response.data.errors.images.includes('Images is invalid') } catch (e) { }
        try { limitExceeded = limitExceeded || err.response.data.errors.attachments.includes('Attachments is invalid') } catch (e) { }
        this.updateFiles(connectionId, false, -1, limitExceeded);
      });
    } else {
      this.updateFiles(connectionId, false, setProgress);
    }
  };

  parseUpdatedFile = (file, library) => {
    // Matching by `file_id` is what identifies a row this visit just uploaded: `inTransit` holds the
    // batch the save was made for, so a hit here means the server object in hand is the persisted
    // form of a file that went through Uppy a moment ago. Every later save falls to the second
    // lookup instead, and the mark is carried by the set rather than re-derived.
    const uploadedNow = this.inTransit[library].find(oldFile => oldFile.file_id && oldFile.file_id === file.file_id);
    const oldFile = uploadedNow
      || this.files[library].find(oldFile => oldFile.id === file.id && oldFile.library === library);
    const uid = `${library}/${file.id}`;

    if (uploadedNow && file.id) {
      this.justUploaded.add(uid);
    }

    if (oldFile) {
      return {
        ...(oldFile.file_id && oldFile.uppyId && { uppyId: oldFile.uppyId }),
        selected: !!oldFile.selected,
        inMediaKit: !!oldFile.inMediaKit,
        ...file,
        library,
        justUploaded: this.justUploaded.has(uid),
      };
    }
    return {
      selected: false,
      inMediaKit: false,
      ...file,
      library,
      justUploaded: this.justUploaded.has(uid),
    };
  };

  updateFiles = (connectionId, files, setProgress = 100, limitExceeded = false) => {
    // console.log('this.files[library]:', Object.assign({}, this.files[library]));
    // console.log('files.attachments:', JSON.stringify(files.attachments, null, '  '));
    // console.log('this.files.attachments:', JSON.stringify(this.files.attachments, null, '  '));
    const mk = files['media_kit'] || [];

    if (files) {
      this.files = {
        images: [
          ...(!files.images ? [] : files.images.map(file => this.parseUpdatedFile(file, 'images'))),
        ],
        attachments: [
          ...(!files.attachments ? [] : files.attachments.map(file => this.parseUpdatedFile(file, 'attachments'))),
        ],
      };


      this.events.emit('post-processed:updated-files');
    } else {
      this.events.emit('post-processed:no-files');
    }

    if (connectionId) {
      this.inTransit.images = this.inTransit.images.filter(file => file.connectionId !== connectionId)
      this.inTransit.attachments = this.inTransit.attachments.filter(file => file.connectionId !== connectionId)

      // The batch's persisted rows are in `this.files` now, so the placeholders they replace go in
      // the same render — dropping them at `'complete'` would leave the list empty in between.
      this.pendingMediaKit.forEach((entry, key) => {
        entry.connectionId === connectionId && this.pendingMediaKit.delete(key);
      });
    }

    this.renderLists();
    typeof setProgress === 'number' && this.updateProgress(setProgress, limitExceeded);
  };

  initLists() {
    if (this.list.source && this.list.target) {
      compileTemplate(this.list);
    }
    if (this.mediaKitList.source && this.mediaKitList.target) {
      compileTemplate(this.mediaKitList);
    }
    this.renderLists();
  };

  getFileByUid(uid) {
    if (!uid) {
      console.warn('error updating file description (no id)');
      return false;
    }

    const output = {};

    const [parts, library, id] = /^([^/]+)\/(.*)$/.exec(uid);

    // for (const library in this.files) {
    const index = this.files[library].findIndex(file => file.id === parseInt(id));
    if (index > -1) {
      Object.assign(output, { library, index, file: this.files[library][index] });
      // break;
    }
    // }

    if (typeof output.index === 'undefined') {
      console.warn('error updating file description (file not found)');
      return false;
    }

    return output;
  };

  confirmFileDeletion(fileDef) {
    return new Promise((resolve, reject) => {
      const reasons = [];
      typeof this.isFileUsed === 'function' && this.isFileUsed(fileDef.file.url) && reasons.push('editor');
      fileDef.file.inMediaKit && reasons.push('media-kit');

      this.dom.confirmDeletion.setContent(fileDef.file.name, reasons);
      this.dom.confirmDeletion.action = confirmed => {
        this.dom.confirmDeletion.action = null;
        resolve(confirmed);
      };
      this.dom.confirmDeletion.showModal();
    })
  };

  // The one write path for a file description, whatever wrote it: the gallery table's inline input
  // (below) or the media-kit row's popup. `handleBatchUploadSuccess(null)` is the save — it re-renders
  // the lists and sends the ordinary PUT /releases/:id with the whole file set on it.
  setFileDescription(forUid, value) {
    const fileDef = this.getFileByUid(forUid);

    if (fileDef) {
      fileDef.file.description = value;
    }

    this.handleBatchUploadSuccess(null);
  };

  handleFileDescriptionUpdate = e => {
    this.setFileDescription(e.currentTarget.dataset.forUid, e.currentTarget.value);
  };

  // Media-kit row popups ————————————————————————————————————————————————————
  //
  // The row shows its description and previews its file; both editing and viewing happen in a modal
  // so the card stays a card. Images get the preview modal, everything else its own tab — a PDF or
  // a spreadsheet has nothing to show inline.

  handleMediaKitFilePreview = e => {
    const { fileUrl, fileName, fileKind } = e.currentTarget.dataset;
    if (!fileUrl) return;

    if (fileKind !== 'image' || !this.dom.mediaKitPreview) {
      window.open(fileUrl, '_blank', 'noopener');
      return;
    }

    this.dom.mediaKitPreviewTitle.textContent = fileName || '';
    this.dom.mediaKitPreviewImage.src = fileUrl;
    this.dom.mediaKitPreviewImage.alt = fileName || '';
    this.dom.mediaKitPreview.show();
  };

  handleMediaKitDescriptionEdit = e => {
    const uid = e.currentTarget.dataset.forUid;
    const fileDef = this.getFileByUid(uid);

    if (!fileDef || !this.dom.mediaKitDescription) return;

    this.editedDescriptionUid = uid;
    this.dom.mediaKitDescriptionFile.textContent = fileDef.file.name || '';
    this.dom.mediaKitDescriptionField.value = fileDef.file.description || '';
    this.dom.mediaKitDescription.show();
  };

  handleMediaKitDescriptionSave = e => {
    e.preventDefault();

    const uid = this.editedDescriptionUid;
    if (!uid) return;

    const value = this.dom.mediaKitDescriptionField.value;
    this.editedDescriptionUid = null;
    this.dom.mediaKitDescription.hide();
    this.setFileDescription(uid, value);
  };

  handleFileDeletion = e => {
    const fileDef = this.getFileByUid(e.currentTarget.dataset.forUid);

    if (fileDef) {
      this.confirmFileDeletion(fileDef)
        .then(confirmed => {
          if (confirmed) {
            this.onFileDeletion(fileDef.file.url, fileDef.file);
            this.files[fileDef.library].splice(fileDef.index, 1);
            fileDef.file.uppyId && this.uppy.removeFile(fileDef.file.uppyId);
            this.handleBatchUploadSuccess(null);
          }
        });
    }
  };

  handleFileSelection = e => {
    const fileDef = this.getFileByUid(e.currentTarget.dataset.forUid);
    // console.log('SELECTING fileDef:', fileDef)
    // console.log('SELECTING fileDef.id, fileDef.uppyId:', fileDef.id, fileDef.uppyId)
    if (fileDef) {
      fileDef.file.selected = e.currentTarget.checked;
    }
  };

  handleMediaKitSelection = e => {
    const fileDef = this.getFileByUid(e.currentTarget.dataset.forUid);
    if (fileDef) {
      fileDef.file.inMediaKit = e.currentTarget.checked;
    }

    this.handleBatchUploadSuccess(null);
  };

  renderLists() {
    if (this.list.template) {
      renderTpl(this.list, this.files);

      Array.from(this.list.target.querySelectorAll('.file-description')).forEach(elem => {
        elem.addEventListener('change', this.handleFileDescriptionUpdate);
      });
      Array.from(this.list.target.querySelectorAll('.file-remove')).forEach(elem => {
        elem.addEventListener('click', this.handleFileDeletion);
      });
      Array.from(this.list.target.querySelectorAll('.file-select')).forEach(elem => {
        elem.addEventListener('change', this.handleFileSelection);
      });

      Array.from(this.list.target.querySelectorAll('.media-kit-select')).forEach(elem => {
        elem.addEventListener('change', this.handleMediaKitSelection);
      });
    }

    if (this.mediaKitList.template) {
      renderTpl(this.mediaKitList, {
        images: this.files.images.filter(file => file.inMediaKit),
        attachments: this.files.attachments.filter(file => file.inMediaKit),
        pending: Array.from(this.pendingMediaKit.values()),
      });

      // `renderTpl` replaces the list's innerHTML, so the row handlers have to be attached again on
      // every render. The X and the description button only exist on saved rows (`{{#if id}}`) —
      // both resolve the file through `data-for-uid`, which needs a real id — while the preview
      // reads its file straight off the button and only needs the URL.
      Array.from(this.mediaKitList.target.querySelectorAll('.file-remove')).forEach(elem => {
        elem.addEventListener('click', this.handleFileDeletion);
      });
      Array.from(this.mediaKitList.target.querySelectorAll('.media-kit-file-preview')).forEach(elem => {
        elem.addEventListener('click', this.handleMediaKitFilePreview);
      });
      Array.from(this.mediaKitList.target.querySelectorAll('.media-kit-file-describe')).forEach(elem => {
        elem.addEventListener('click', this.handleMediaKitDescriptionEdit);
      });
    }
  };

  // In-flight media-kit rows ————————————————————————————————————————————————
  //
  // Between a drop and the save that follows the upload there is nothing to render a row from:
  // `file-added` carries no library, no file id and no server metadata. These four methods keep
  // just enough of Uppy's own state to draw a placeholder, and drop it again once the persisted
  // row exists.

  trackPendingMediaKitFile(file) {
    if (!this.mediaKitList.template || !file || file.source !== MEDIA_KIT_DROP_SOURCE) return;

    const kind = PENDING_FILE_KINDS.find(entry => entry.match(file.type || '')) || PENDING_FILE_KIND_FALLBACK;

    this.pendingMediaKit.set(file.id, {
      pending: true,
      pendingUid: file.id,
      name: file.name,
      kind: kind.kind,
      icon: kind.icon,
      percent: 0,
    });

    this.renderLists();
  };

  updatePendingMediaKitProgress(file, progress) {
    const entry = file && this.pendingMediaKit.get(file.id);
    if (!entry) return;

    const total = (progress && progress.bytesTotal) || 0;
    if (total) {
      entry.percent = Math.min(100, Math.round(((progress.bytesUploaded || 0) / total) * 100));
    }

    this.paintPendingMediaKitPercent(file.id, entry.percent);
  };

  holdPendingMediaKitFile(file, connectionId) {
    const entry = file && this.pendingMediaKit.get(file.id);
    if (!entry) return;

    entry.percent = 100;
    entry.connectionId = connectionId;
    this.paintPendingMediaKitPercent(file.id, entry.percent);
  };

  // Written straight onto the row's own nodes: a re-render per progress tick would rebuild every
  // card in the list several times a second — and restart the bar's transition each time. Uppy ids
  // are matched by reading the attribute rather than by selector, so nothing has to be escaped.
  paintPendingMediaKitPercent(uppyId, percent) {
    const row = Array.from(this.mediaKitList.target.querySelectorAll('[data-pending-uid]'))
      .find(elem => elem.dataset.pendingUid === uppyId);
    if (!row) return;

    const percentNode = row.querySelector('.media-kit-file-percent');
    const barNode = row.querySelector('.media-kit-file-progress-bar');
    const meterNode = row.querySelector('[role="progressbar"]');

    if (percentNode) {
      percentNode.textContent = `${percent}%`;
    }
    if (barNode) {
      barNode.style.width = `${percent}%`;
    }
    if (meterNode) {
      meterNode.setAttribute('aria-valuenow', percent);
    }
  };

  forgetPendingMediaKitFile(file) {
    if (!file || !this.pendingMediaKit.delete(file.id)) return;

    this.renderLists();
  };

  // Media-kit drop zone ——————————————————————————————————————————————————————
  //
  // Uppy 1.x here has no DragDrop plugin, but `addFile` ingests plain `File` objects and
  // `autoProceed` uploads them, so drag-and-drop needs nothing new — only a source string the rest
  // of the pipeline recognises.

  initMediaKitDropZone(elem) {
    if (!elem) return;

    this.dom.mediaKitDropZone = elem;
    this.initMediaKitFileInput(elem);

    ['dragenter', 'dragover'].forEach(eventName => {
      elem.addEventListener(eventName, e => {
        e.preventDefault();
        e.stopPropagation();
        elem.classList.add('is-dragover');
      });
    });

    ['dragleave', 'dragend'].forEach(eventName => {
      elem.addEventListener(eventName, () => elem.classList.remove('is-dragover'));
    });

    elem.addEventListener('drop', e => {
      e.preventDefault();
      e.stopPropagation();
      elem.classList.remove('is-dragover');
      this.addFilesToMediaKit(Array.from((e.dataTransfer && e.dataTransfer.files) || []));
    });

    elem.addEventListener('click', e => {
      // `pickFilesForMediaKit` clicks the hidden input, and that click bubbles straight back through
      // here. Without this the zone would forward it again, and again, forever.
      if (e.target === this.dom.mediaKitFileInput) return;

      e.preventDefault();
      this.pickFilesForMediaKit();
    });

    elem.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.pickFilesForMediaKit();
      }
    });
  };

  // The zone's picker is a real input living in the drop-zone markup (releases/form/_content), not
  // one built per click: a detached input is clickable in Chromium but not something WebKit
  // promises, and the file chooser is the whole way into the kit.
  initMediaKitFileInput(elem) {
    const input = elem.querySelector('.media-kit-dropzone-input');
    if (!input) return;

    this.dom.mediaKitFileInput = input;
    // Written here rather than in the view so the allowed types are spelled out once — this is the
    // same list `isAllowedMediaKitFile` re-checks against, `accept` only being a hint to the chooser.
    input.accept = this.defaultConfig.uppy.restrictions.allowedFileTypes.join(',');

    input.addEventListener('change', () => {
      const files = Array.from(input.files || []);
      // The chooser holds its selection, and an unchanged selection fires no `change` — so a file
      // removed from the kit and picked again would do nothing at all. The `File` objects just read
      // out of it stay valid; only the input's own list is cleared.
      input.value = '';
      this.addFilesToMediaKit(files);
    });
  };

  pickFilesForMediaKit() {
    this.dom.mediaKitFileInput && this.dom.mediaKitFileInput.click();
  };

  addFilesToMediaKit(files) {
    const accepted = files.filter(file => this.isAllowedMediaKitFile(file));

    if (!accepted.length) return;

    // A content-file upload leaves the restrictions narrowed and `uploadingContentFile` set; a drop
    // must never inherit either.
    this.resetOptions();

    accepted.forEach(file => {
      // Uppy refuses a file it already holds by throwing, and a throw here would abandon the rest of
      // the batch — pick one file already in the kit alongside three new ones and the three would
      // never be added. A refusal is about that one file and stops with it.
      try {
        this.uppy.addFile({ source: MEDIA_KIT_DROP_SOURCE, name: file.name, type: file.type, data: file });
      } catch (error) {
        console.warn('media kit: file not added', file.name, error);
      }
    });
  };

  isAllowedMediaKitFile(file) {
    const extension = `.${(file.name || '').split('.').pop()}`.toLowerCase();
    const allowed = this.defaultConfig.uppy.restrictions.allowedFileTypes.some(rule => (
      rule.endsWith('/*') ? (file.type || '').startsWith(rule.slice(0, -1)) : rule === extension || rule === file.type
    ));

    if (!allowed) {
      this.notifyFileRejected('upload-typeRejected', file.name);
      return false;
    }

    if (file.size > (/^image\//.test(file.type) ? MAX_IMAGE_SIZE : MAX_FILE_SIZE)) {
      this.notifyFileRejected('upload-sizeRejected', file.name);
      return false;
    }

    return true;
  };

  // An image over the size or dimension limit leaves the upload, and the batch's refusals are named
  // in one message. The gallery route has no other size check in the browser. `removeFile` takes the
  // file out of the running upload, and Uppy drops the upload when no file is left in it.
  refuseOversizedImages = async fileIDs => {
    const refused = { 'upload-sizeRejected': [], 'upload-dimensionsRejected': [] };

    await Promise.all(fileIDs.map(async fileID => {
      const file = this.uppy.getFile(fileID);
      if (!file || !/^image\//.test(file.type || '')) return;

      let subject = null;
      if (file.size > MAX_IMAGE_SIZE) {
        subject = 'upload-sizeRejected';
      } else {
        const dimensions = await readImageDimensions(file.data);
        if (dimensions && (dimensions.width > MAX_IMAGE_DIMENSION || dimensions.height > MAX_IMAGE_DIMENSION)) {
          subject = 'upload-dimensionsRejected';
        }
      }

      if (subject) {
        refused[subject].push(file.name);
        this.uppy.removeFile(fileID);
      }
    }));

    const refusedCount = refused['upload-sizeRejected'].length + refused['upload-dimensionsRejected'].length;
    if (!refusedCount) return;

    this.notifyFilesRefused(refused);
    // A dropped upload never reaches 'complete', so nothing else would close it — and the admin
    // editor's `uploadImage` waits on a `post-processed` event.
    if (refusedCount === fileIDs.length) {
      this.events.emit('post-processed:refused');
    }
  };

  notifyFilesRefused(refused) {
    const subjects = Object.keys(refused).filter(subject => refused[subject].length);
    const names = subjects.flatMap(subject => refused[subject]);

    if (this.notifier) {
      // One message, a line per reason; the dict's `{{filename}}` takes every name refused for it.
      const message = subjects.map(subject => {
        const template = (this.notifier.dict[subject] || {}).message || '';
        return template.replace('{{filename}}', () => refused[subject].map(escapeHtml).join(', '));
      }).join('<br>');
      this.notifier.show(subjects[0], { message });
    } else {
      // The admin's uploader has no notifier, only this line beside its button.
      this.dom.progress.textContent = `Not uploaded: ${names.join(', ')}. Images can be up to `
        + `${MAX_IMAGE_SIZE / 1024 / 1024} MB and ${MAX_IMAGE_DIMENSION} × ${MAX_IMAGE_DIMENSION} px.`;
    }

    this.refusalShown = true;
  };

  notifyFileRejected(subject, filename) {
    if (!this.notifier) return;

    const message = (this.notifier.dict[subject] || {}).message;
    this.notifier.show(subject, message && { message: message.replace('{{filename}}', escapeHtml(filename)) });
  };

  setSelections(newSelections = [], options = {}) {
    // if(typeof options.limit !== 'undefined'){
    //   this.selectionLimit = options.limit;
    // }

    this.files.images.forEach(file => {
      file.selected = newSelections.findIndex(sel => sel.id === file.id && sel.library === 'images') > -1;
    });
    this.files.attachments.forEach(file => {
      file.selected = newSelections.findIndex(sel => sel.id === file.id && sel.library === 'attachments') > -1;
    });

    this.renderLists();
  };

  getFiles() {
    return [
      ...this.files.images.map(file => ({ ...file, library: 'images' })),
      ...this.files.attachments.map(file => ({ ...file, library: 'attachments' })),
    ];
  };

  getContentFile() {
    const result = this.files.attachments.find(file => file.content);
    return result && { ...result, library: 'attachments' };
  };

  getSelectedFiles() {
    return [
      ...this.files.images.filter(file => file.selected).map(file => ({ ...file, library: 'images' })),
      ...this.files.attachments.filter(file => file.selected).map(file => ({ ...file, library: 'attachments' })),
    ];
  };

  getFilesForShrine() {
    return {
      images: this.files.images.map(API.parseFile.forUpdateAPI).concat(this.inTransit.images.filter(file => !file.sent).map(API.parseFile.forUpdateAPI)),
      attachments: this.files.attachments.map(API.parseFile.forUpdateAPI).concat(this.inTransit.attachments.filter(file => !file.sent).map(API.parseFile.forUpdateAPI)),
      'media_kit': [
        ...this.files.images.filter(file => file.inMediaKit && file.id).map(file => ({ id: file.id, library: file.library })),
        ...this.files.attachments.filter(file => file.inMediaKit && file.id).map(file => ({ id: file.id, library: file.library })),
      ],
    };
  };

  uploadImage() {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = this.defaultConfig.uppyOneImage.restrictions.allowedFileTypes.join(',');

      const handleChange = () => {
        // console.log('Change');
        this.uploadImage_callback(resolve, reject, input, Array.from(input.files));
      };

      input.addEventListener('change', handleChange);

      // console.log('gonna click', input === document.activeElement, input)
      input.click();
    });
  };

  uploadImage_callback(resolve, reject, input, files) {
    // console.log('uploadImage_callback')
    if (files && Array.isArray(files) && files.length > 0) {
      let uploads = files.map(file => ({
        uppyId: this.uppy.addFile({ source: 'method:uploadImage', name: file.name, type: file.type, data: file }),
      }));

      input.remove();

      this.events.once('post-processed', () => {
        // console.log('post-processed', this.files.images)
        uploads = uploads.map(upload => {
          // console.log('upload:', upload)
          const file = this.files.images.find(file => file.uppyId === upload.uppyId);
          // console.log('upload, file:', upload, file, this.files.images)
          if (file && file.url) {
            return {
              url: file.url,
              library: 'images',
            };
          } else {
            return false;
          }
        }).filter(Boolean);

        // console.log('uploads:', uploads)

        if (uploads.length) {
          // console.log('resolve:', typeof resolve, resolve)
          resolve(uploads);
        } else {
          // console.log('reject:', typeof reject, reject)
          reject('no uploads found');
        }
      });
    } else {
      reject('callback got no files');
    }

    input.remove();
  };
};